import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtCurrency, fmtDate } from "../../lib/api";
import MobileHeader from "../../components/MobileHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { useI18n } from "../../lib/i18n";
import {
    LineChart, Receipt, Wallet as WalletIcon, TrendingUp, ChevronRight,
    Clock, CheckCircle2, Plus, ShieldCheck, Sparkles,
} from "lucide-react";

export default function Investments() {
    const nav = useNavigate();
    const { t } = useI18n();
    const [tab, setTab] = useState("tbills");
    const [portfolio, setPortfolio] = useState(null);
    const [catalog, setCatalog] = useState(null);
    const [tbills, setTbills] = useState([]);
    const [bonds, setBonds] = useState([]);
    const [mmfs, setMmfs] = useState([]);
    const [dlg, setDlg] = useState(null);

    const load = async () => {
        const [p, c, t, b, m] = await Promise.all([
            api.get("/investments/portfolio"),
            api.get("/investments/catalog"),
            api.get("/investments/tbills"),
            api.get("/investments/bonds"),
            api.get("/investments/mmfs"),
        ]);
        setPortfolio(p.data);
        setCatalog(c.data);
        setTbills(t.data);
        setBonds(b.data);
        setMmfs(m.data);
    };
    useEffect(() => { load(); }, []);

    if (!portfolio || !catalog) return <div className="p-12 text-center text-slate-400">Loading…</div>;

    return (
        <div data-testid="investments-page">
            <MobileHeader title={t("inv.title")} backTo="/consumer" variant="amber">
                <div className="mt-4 grid grid-cols-3 gap-2">
                    <Kpi label={t("inv.kpi.portfolio")} value={fmtCurrency(portfolio.totals.grand_total, "NGN", { compact: true })} />
                    <Kpi label={t("inv.kpi.projected")} value={fmtCurrency(portfolio.totals.projected_returns, "NGN", { compact: true })} accent />
                    <Kpi label={t("inv.kpi.holdings")} value={portfolio.counts.tbills + portfolio.counts.bonds + portfolio.counts.mmfs} />
                </div>
            </MobileHeader>

            <div className="px-5 -mt-4 pb-4 space-y-4">
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className="bg-white border border-slate-200 rounded-xl p-1 grid grid-cols-3 w-full" data-testid="investments-tabs">
                        <TabsTrigger value="tbills" data-testid="tab-tbills"><Receipt size={14} /> {t("inv.tab.tbills")}</TabsTrigger>
                        <TabsTrigger value="bonds" data-testid="tab-bonds"><LineChart size={14} /> {t("inv.tab.bonds")}</TabsTrigger>
                        <TabsTrigger value="mmfs" data-testid="tab-mmfs"><TrendingUp size={14} /> {t("inv.tab.mmfs")}</TabsTrigger>
                    </TabsList>
                </Tabs>

                {tab === "tbills" && (
                    <>
                        <Button onClick={() => setDlg("tbill")} className="w-full h-12 rounded-xl bg-[#F59E0B] hover:bg-[#d97706] font-semibold" data-testid="buy-tbill-btn">
                            <Plus size={16} /> {t("inv.tbill.buy")}
                        </Button>
                        <div className="grid grid-cols-3 gap-2">
                            {catalog.tbills.map((tt) => (
                                <div key={tt.tenor_days} className="bg-white rounded-2xl p-3 border border-slate-200/60 text-center product-shadow">
                                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{tt.tenor_days}d</div>
                                    <div className="font-display text-xl font-bold text-[#F59E0B] mt-1">{(tt.discount_rate * 100).toFixed(1)}%</div>
                                    <div className="text-[10px] text-slate-500">discount</div>
                                </div>
                            ))}
                        </div>
                        {tbills.length === 0 && <EmptyHint msg={t("inv.tbill.empty")} />}
                        <div className="space-y-3">
                            {tbills.map((tt) => <TbillCard key={tt.id} t={tt} />)}
                        </div>
                    </>
                )}

                {tab === "bonds" && (
                    <>
                        <Button onClick={() => setDlg("bond")} className="w-full h-12 rounded-xl bg-[#0066FF] hover:bg-[#0052cc] font-semibold" data-testid="buy-bond-btn">
                            <Plus size={16} /> {t("inv.bond.buy")}
                        </Button>
                        {bonds.length === 0 && <EmptyHint msg={t("inv.bond.empty")} />}
                        <div className="space-y-3">
                            {bonds.map((b) => <BondCard key={b.id} b={b} />)}
                        </div>
                    </>
                )}

                {tab === "mmfs" && (
                    <>
                        <Button onClick={() => setDlg("mmf")} className="w-full h-12 rounded-xl bg-[#7000FF] hover:bg-[#5d00d6] font-semibold" data-testid="buy-mmf-btn">
                            <Plus size={16} /> {t("inv.mmf.invest")}
                        </Button>
                        {mmfs.length === 0 && <EmptyHint msg={t("inv.mmf.empty")} />}
                        <div className="space-y-3">
                            {mmfs.map((m) => <MmfCard key={m.id} m={m} onReload={load} />)}
                        </div>
                    </>
                )}
            </div>

            <TbillDialog open={dlg === "tbill"} onClose={() => setDlg(null)} catalog={catalog} onDone={load} />
            <BondDialog open={dlg === "bond"} onClose={() => setDlg(null)} catalog={catalog} onDone={load} />
            <MmfDialog open={dlg === "mmf"} onClose={() => setDlg(null)} catalog={catalog} onDone={load} />
        </div>
    );
}

function Kpi({ label, value, accent }) {
    return (
        <div className="bg-white/15 backdrop-blur rounded-xl px-2 py-2 text-center">
            <div className="text-[10px] text-white/80 uppercase tracking-wider">{label}</div>
            <div className={`text-sm font-bold ${accent ? "text-emerald-200" : "text-white"} truncate`}>{value}</div>
        </div>
    );
}

function EmptyHint({ msg }) {
    return <div className="bg-white rounded-2xl p-6 text-center text-sm text-slate-500 border border-slate-200/60 product-shadow">{msg}</div>;
}

function TbillCard({ t: tb }) {
    const { t } = useI18n();
    const STATUS_BG = { active: "bg-blue-50 text-blue-700 border-blue-200", matured: "bg-emerald-50 text-emerald-700 border-emerald-200" };
    return (
        <div className="bg-white rounded-2xl p-4 border border-slate-200/60 product-shadow" data-testid={`tbill-${tb.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="font-display font-semibold text-[#0A1930]">{tb.label} · {(tb.discount_rate * 100).toFixed(1)}% discount</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                        <Clock size={10} /> {fmtDate(tb.maturity_date)}
                    </div>
                </div>
                <Badge className={`capitalize border ${STATUS_BG[tb.status] || "bg-slate-100 text-slate-700"}`}>{tb.status}</Badge>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 pt-3 border-t border-slate-100">
                <Mini label={t("inv.col.paid")} value={fmtCurrency(tb.purchase_price, "NGN", { compact: true })} />
                <Mini label={t("inv.col.discount")} value={`+${fmtCurrency(tb.discount_earned, "NGN", { compact: true })}`} valueClass="text-emerald-600" />
                <Mini label={t("inv.col.at_maturity")} value={fmtCurrency(tb.face_value, "NGN", { compact: true })} valueClass="text-[#F59E0B]" />
            </div>
        </div>
    );
}

function BondCard({ b }) {
    const { t } = useI18n();
    return (
        <div className="bg-white rounded-2xl p-4 border border-slate-200/60 product-shadow" data-testid={`bond-${b.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="font-display font-semibold text-[#0A1930]">{b.name}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{b.tenure_years}y · {b.coupon_freq} · {(b.coupon_rate * 100).toFixed(1)}% coupon</div>
                </div>
                <Badge className="capitalize border bg-blue-50 text-blue-700 border-blue-200">{b.status}</Badge>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 pt-3 border-t border-slate-100">
                <Mini label={t("inv.col.principal")} value={fmtCurrency(b.amount, "NGN", { compact: true })} />
                <Mini label={t("inv.col.total_coupons")} value={`+${fmtCurrency(b.total_coupons, "NGN", { compact: true })}`} valueClass="text-emerald-600" />
                <Mini label={t("inv.col.at_maturity")} value={fmtCurrency(b.matured_value, "NGN", { compact: true })} valueClass="text-[#0066FF]" />
            </div>
            {b.next_coupon_in_days != null && (
                <div className="mt-2 text-[10px] text-slate-500 text-center">Next coupon in {b.next_coupon_in_days}d · {b.coupons_paid || 0} paid so far</div>
            )}
        </div>
    );
}

function MmfCard({ m, onReload }) {
    const { t } = useI18n();
    const [redeemAll, setRedeemAll] = useState(true);
    const [amount, setAmount] = useState("");
    const [busy, setBusy] = useState(false);
    const redeem = async () => {
        setBusy(true);
        try {
            const body = redeemAll ? { holding_id: m.id } : { holding_id: m.id, units: parseFloat(amount) };
            const r = await api.post("/investments/mmfs/redeem", body);
            toast.success(`${t("inv.mmf.redeem")} ${fmtCurrency(r.data.payout)}`);
            setAmount("");
            onReload();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };
    return (
        <div className="bg-white rounded-2xl p-4 border border-slate-200/60 product-shadow" data-testid={`mmf-${m.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="size-11 rounded-xl flex items-center justify-center text-white flex-shrink-0" style={{ background: m.color }}>
                        <TrendingUp size={20} />
                    </div>
                    <div className="min-w-0">
                        <div className="font-display font-semibold text-[#0A1930] truncate">{m.name}</div>
                        <div className="text-[11px] text-slate-500">{(m.yield_apr * 100).toFixed(1)}% APR · {m.risk} risk</div>
                        {m.yield_boost_pct > 0 && (
                            <div
                                className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-md bg-emerald-50 border border-emerald-200 text-[9px] font-bold text-emerald-700 uppercase tracking-wider"
                                data-testid={`mmf-boost-${m.id}`}
                            >
                                <Sparkles size={9} /> {m.tier_at_purchase} +{(m.yield_boost_pct * 100).toFixed(0)}% boost
                            </div>
                        )}
                    </div>
                </div>
                <Badge className="capitalize border bg-purple-50 text-purple-700 border-purple-200">{m.status}</Badge>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 pt-3 border-t border-slate-100">
                <Mini label={t("inv.col.principal")} value={fmtCurrency(m.principal, "NGN", { compact: true })} />
                <Mini label={t("inv.col.interest")} value={`+${fmtCurrency(m.interest_accrued, "NGN", { compact: true })}`} valueClass="text-emerald-600" />
                <Mini label={t("inv.col.now")} value={fmtCurrency(m.current_value, "NGN", { compact: true })} valueClass="text-[#7000FF]" />
            </div>
            {m.status === "active" && (
                <div className="mt-3 flex items-center gap-2">
                    <button onClick={() => setRedeemAll(!redeemAll)} className={`text-[10px] px-2 py-1 rounded-md font-semibold ${redeemAll ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-600"}`} data-testid={`mmf-toggle-${m.id}`}>
                        {redeemAll ? t("inv.mmf.full") : t("inv.mmf.partial")}
                    </button>
                    {!redeemAll && <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="…" className="h-9 text-xs rounded-lg" />}
                    <Button onClick={redeem} disabled={busy || (!redeemAll && !amount)} size="sm" className="h-9 rounded-lg bg-[#7000FF] hover:bg-[#5d00d6] text-xs ml-auto" data-testid={`mmf-redeem-${m.id}`}>
                        {t("inv.mmf.redeem")}
                    </Button>
                </div>
            )}
        </div>
    );
}

function Mini({ label, value, valueClass }) {
    return (
        <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
            <div className={`font-mono text-xs font-bold mt-0.5 ${valueClass || "text-[#0A1930]"}`}>{value}</div>
        </div>
    );
}

function TbillDialog({ open, onClose, catalog, onDone }) {
    const { t } = useI18n();
    const [tenor, setTenor] = useState(91);
    const [face, setFace] = useState("");
    const [busy, setBusy] = useState(false);
    const meta = catalog.tbills.find((tt) => tt.tenor_days === tenor);
    const faceN = parseFloat(face || 0);
    const purchase = faceN ? faceN / (1 + meta.discount_rate * tenor / 365.0) : 0;
    const discount = faceN - purchase;
    const submit = async () => {
        if (faceN <= 0) return toast.error("Enter face value");
        setBusy(true);
        try {
            await api.post("/investments/tbills", { tenor_days: tenor, face_value: faceN });
            toast.success(`T-Bill purchased — earn ${fmtCurrency(discount)} at maturity`);
            setFace("");
            onDone(); onClose();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-2xl max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2"><Receipt size={18} className="text-[#F59E0B]" /> {t("inv.dlg.tbill.title")}</DialogTitle>
                    <DialogDescription>{t("inv.dlg.tbill.desc")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                        {catalog.tbills.map((tt) => (
                            <button key={tt.tenor_days} onClick={() => setTenor(tt.tenor_days)} className={`p-3 rounded-xl border text-center ${tenor === tt.tenor_days ? "border-[#F59E0B] bg-amber-50" : "border-slate-200"}`} data-testid={`tbill-tenor-${tt.tenor_days}`}>
                                <div className="font-display text-xl font-bold text-[#F59E0B]">{(tt.discount_rate * 100).toFixed(1)}%</div>
                                <div className="text-[10px] text-slate-500 uppercase font-bold">{tt.tenor_days}d</div>
                            </button>
                        ))}
                    </div>
                    <div>
                        <Label className="text-xs">{t("inv.dlg.tbill.face")}</Label>
                        <Input type="number" value={face} onChange={(e) => setFace(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="tbill-face-input" />
                    </div>
                    {faceN > 0 && (
                        <div className="bg-amber-50 rounded-xl p-3 text-sm">
                            <div className="flex justify-between"><span className="text-slate-600">{t("inv.dlg.tbill.pay_today")}</span><span className="font-bold">{fmtCurrency(purchase)}</span></div>
                            <div className="flex justify-between mt-1"><span className="text-slate-600">{t("inv.dlg.tbill.discount_earned")}</span><span className="font-bold text-emerald-600">+{fmtCurrency(discount)}</span></div>
                            <div className="flex justify-between mt-1"><span className="text-slate-600">{t("inv.dlg.tbill.receive")}</span><span className="font-bold text-[#F59E0B]">{fmtCurrency(faceN)}</span></div>
                        </div>
                    )}
                    <Button onClick={submit} disabled={busy} className="w-full h-11 rounded-xl bg-[#F59E0B] hover:bg-[#d97706]" data-testid="tbill-submit-btn">
                        {busy ? "…" : t("inv.tbill.buy")}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function BondDialog({ open, onClose, catalog, onDone }) {
    const { t } = useI18n();
    const [productKey, setProductKey] = useState("FGN-2YR");
    const [amount, setAmount] = useState("");
    const [busy, setBusy] = useState(false);
    const meta = catalog.bonds.find((b) => b.key === productKey);
    const amt = parseFloat(amount || 0);
    const totalCoupons = amt && meta ? amt * meta.coupon_rate * meta.tenure_years : 0;
    const submit = async () => {
        if (amt < (meta?.min_amount || 1)) return toast.error(`Min ${fmtCurrency(meta.min_amount)}`);
        setBusy(true);
        try {
            await api.post("/investments/bonds", { product_key: productKey, amount: amt });
            toast.success(`Bond purchased — projected return ${fmtCurrency(totalCoupons)}`);
            setAmount("");
            onDone(); onClose();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-2xl max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2"><LineChart size={18} className="text-[#0066FF]" /> {t("inv.dlg.bond.title")}</DialogTitle>
                    <DialogDescription>{t("inv.dlg.bond.desc")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-2">
                        {catalog.bonds.map((b) => (
                            <button key={b.key} onClick={() => setProductKey(b.key)} className={`w-full p-3 rounded-xl border text-left flex items-center justify-between ${productKey === b.key ? "border-[#0066FF] bg-blue-50" : "border-slate-200"}`} data-testid={`bond-product-${b.key}`}>
                                <div>
                                    <div className="font-display font-semibold text-sm">{b.name}</div>
                                    <div className="text-[10px] text-slate-500">Min {fmtCurrency(b.min_amount, "NGN", { compact: true })}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-bold text-[#0066FF]">{(b.coupon_rate * 100).toFixed(1)}%</div>
                                    <div className="text-[10px] text-slate-500">{b.tenure_years}y</div>
                                </div>
                            </button>
                        ))}
                    </div>
                    <div>
                        <Label className="text-xs">{t("inv.dlg.bond.amount")}</Label>
                        <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="bond-amount-input" />
                    </div>
                    {amt > 0 && meta && (
                        <div className="bg-blue-50 rounded-xl p-3 text-sm">
                            <div className="flex justify-between"><span className="text-slate-600">{t("inv.dlg.bond.coupons", { n: meta.tenure_years })}</span><span className="font-bold text-emerald-600">+{fmtCurrency(totalCoupons)}</span></div>
                            <div className="flex justify-between mt-1"><span className="text-slate-600">{t("inv.dlg.bond.final")}</span><span className="font-bold text-[#0066FF]">{fmtCurrency(amt + totalCoupons)}</span></div>
                        </div>
                    )}
                    <Button onClick={submit} disabled={busy} className="w-full h-11 rounded-xl bg-[#0066FF] hover:bg-[#0052cc]" data-testid="bond-submit-btn">
                        {busy ? "…" : t("inv.bond.buy")}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function MmfDialog({ open, onClose, catalog, onDone }) {
    const { t } = useI18n();
    const [fundKey, setFundKey] = useState("STELLAR");
    const [amount, setAmount] = useState("");
    const [busy, setBusy] = useState(false);
    const meta = catalog.mmfs.find((m) => m.key === fundKey);
    const submit = async () => {
        const amt = parseFloat(amount || 0);
        if (amt < (meta?.min_amount || 1)) return toast.error(`Min ${fmtCurrency(meta.min_amount)}`);
        setBusy(true);
        try {
            await api.post("/investments/mmfs", { fund_key: fundKey, amount: amt });
            toast.success("Investment booked. Yield accrues daily.");
            setAmount("");
            onDone(); onClose();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-2xl max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2"><TrendingUp size={18} className="text-[#7000FF]" /> {t("inv.dlg.mmf.title")}</DialogTitle>
                    <DialogDescription>{t("inv.dlg.mmf.desc")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-2">
                        {catalog.mmfs.map((m) => (
                            <button key={m.key} onClick={() => setFundKey(m.key)} className={`w-full p-3 rounded-xl border text-left flex items-center justify-between ${fundKey === m.key ? "bg-purple-50" : "border-slate-200"}`} style={{ borderColor: fundKey === m.key ? m.color : undefined, borderWidth: fundKey === m.key ? 2 : 1 }} data-testid={`mmf-fund-${m.key}`}>
                                <div>
                                    <div className="font-display font-semibold text-sm">{m.name}</div>
                                    <div className="text-[10px] text-slate-500">Min {fmtCurrency(m.min_amount, "NGN", { compact: true })} · {m.risk}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-bold" style={{ color: m.color }}>{(m.yield_apr * 100).toFixed(1)}%</div>
                                    <div className="text-[10px] text-slate-500">APR</div>
                                </div>
                            </button>
                        ))}
                    </div>
                    <div>
                        <Label className="text-xs">{t("inv.dlg.mmf.amount")}</Label>
                        <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="mmf-amount-input" />
                    </div>
                    <Button onClick={submit} disabled={busy} className="w-full h-11 rounded-xl bg-[#7000FF] hover:bg-[#5d00d6]" data-testid="mmf-submit-btn">
                        {busy ? "…" : t("inv.dlg.mmf.submit")}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
